<template>
  <div class="bcci__wrapper">
    <system-card
      v-if="systemMessage"
      :system-message="systemMessage"
      :cb-func="loadCard"
    />
    <template v-else>
      <profile-top
        :name="(userInfo ? userInfo.name : personFullName) || ''"
        :position="userInfo ? userInfo.position : cardValues.position"
        :department="userInfo ? userInfo.department : cardValues.department"
        :company="userInfo ? userInfo.company : cardValues.company"
        :photo="userInfo ? userInfo.photo : null"
      />
      <div class="bcci-content">
        <up-card class="bcci-card">
          <h2 class="bcci-card__title">{{ $t('businessCardCollectionItem.contacts') }}</h2>
          <template v-if="contacts.length">
            <div
              v-for="group in contacts"
              :key="group.key"
              class="bcci-contact-group"
            >
              <div class="bcci-contact-group__header">
                <span class="bcci-contact-group__icon-wrap">
                  <i :class="group.icon" />
                </span>
              </div>
              <div class="bcci-contact-group__value-group">
                <div
                  v-for="(value, i) in group.values"
                  :key="i"
                  class="bcci-contact-group__value"
                >
                  <span v-if="i === 0" class="bcci-contact-group__label">{{ group.label }}</span>
                  <a :href="group.toHref(value)" class="bcci-contact-group__link">{{ value }}</a>
                </div>
              </div>
            </div>
          </template>
          <p v-else class="bcci-no-contacts">{{ $t('businessCardCollectionItem.no_contacts') }}</p>
        </up-card>
      </div>
      <div
        v-for="group in extendInfo"
        :key="group.name"
        class="bcci-content"
      >
        <up-card class="bcci-card">
          <h2 class="bcci-card__title bcci-card__title--question">{{ group.label }}</h2>
          <ul class="bcci-extend-list">
            <li
              v-for="(value, i) in group.values"
              :key="i"
              class="bcci-extend-list__item"
            >{{ value }}</li>
          </ul>
        </up-card>
      </div>
      <div class="bcci-actions">
        <up-button
          class="up-btn bcci-btn"
          @click="handleDownloadVCard"
        >
          <i class="exu-download-01 bcci-btn__icon" />
          <span class="bcci-btn__label">
            {{ $t('businessCardCollectionItem.btn_download_vcard') }}
          </span>
        </up-button>
        <up-button
          class="up-btn bcci-btn"
          :disabled="isChatLoading"
          @click="handleGoToChat"
        >
          <span v-if="isChatLoading" class="bcci-btn__spinner" />
          <template v-else>
            <i class="exu-message-chat-square bcci-btn__icon" />
            <span class="bcci-btn__label">{{ $t('businessCardCollectionItem.btn_chat') }}</span>
          </template>
        </up-button>
        <up-button
          class="up-btn bcci-btn"
          @click="handleGoToProfile"
        >
          <i class="exu-users bcci-btn__icon" />
          <span class="bcci-btn__label">{{ $t('businessCardCollectionItem.btn_profile') }}</span>
        </up-button>
      </div>
    </template>
  </div>
</template>

<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import businessCard from '@/components/mixins/methodsBusinessCard';
import methodsChat from '@/components/mixins/methodsChat';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';
import {
  contactGroups, emptyCard, extendGroups, parseCard, personName,
} from '@/components/mixins/businessCardFields';

export default {
  name: 'BusinessCardCollectionItem',
  components: { SystemCard },
  props: {
    userId: { required: true },
    locale: { type: String },
  },
  data() {
    return {
      isLoading: true,
      isChatLoading: false,
      systemMessage: null,
      userInfo: null,
      // состав полей задаёт визитка участника — показываем только пришедшие
      card: emptyCard(),
      exchangedAt: null,
    };
  },
  computed: {
    cardValues() {
      return this.card.values;
    },
    personFullName() {
      return personName(this.card.values);
    },
    contacts() {
      return contactGroups(this.card, (field) => this.fieldLabel(field));
    },
    extendInfo() {
      return extendGroups(this.card);
    },
  },
  mixins: [appBaseNav, businessCard, methodsChat],
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('businessCardCollectionItem.header_title'));
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('businessCardCollectionItem.header_title'));
    this.loadCard();
  },
  methods: {
    loadCard() {
      this.isLoading = true;
      this.systemMessage = null;
      this.getBusinessCardCollectionItem(this.userId)
        .then((data) => {
          this.isLoading = false;
          this.userInfo = data.user || null;
          this.exchangedAt = data.exchanged_at || null;
          // описатели дополнительных полей — рядом с card_data; если их нет,
          // extendFields останутся пустыми и блок просто не отрисуется
          const parsed = parseCard(data.card_data, data.extend_data, this.$store.getters.getLang);
          if (!parsed) {
            this.systemMessage = this.$t('system.bad_connect');
            return;
          }
          this.card = parsed;
        })
        .catch(() => {
          this.isLoading = false;
          this.systemMessage = this.$t('system.bad_connect');
        });
    },
    fieldLabel(field) {
      return field.label || this.$t(field.labelKey);
    },
    handleDownloadVCard() {
      const {
        lname = '', fname = '', sname = '',
        position = '', department = '', company = '',
        phone = [], email = [],
      } = this.cardValues;
      const fullName = this.personFullName;
      const org = [company, department].filter(Boolean).join(';');
      const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${fullName}`,
        // имя по частям: раньше склеенное ФИО разложить было не на что
        `N:${lname};${fname};${sname};;`,
        ...(org ? [`ORG:${org}`] : []),
        ...(position ? [`TITLE:${position}`] : []),
        ...phone.filter(Boolean).map((p) => `TEL:${p}`),
        ...email.filter(Boolean).map((e) => `EMAIL:${e}`),
        'END:VCARD',
      ];
      const blob = new Blob([lines.join('\r\n')], { type: 'text/vcard' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${fullName || 'contact'}.vcf`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
    handleGoToChat() {
      if (!this.userInfo || !this.userInfo.id) return;
      this.isChatLoading = true;
      this.getChatRoomByUser(this.userInfo.id)
        .then((results) => {
          this.$router.push({ name: 'chatView', params: { uuid: results.uuid } });
        })
        .catch(() => {
          this.isChatLoading = false;
        });
    },
    handleGoToProfile() {
      if (!this.userInfo || !this.userInfo.id) return;
      this.$router.push({ name: 'participantProfile', params: { id: this.userInfo.id } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.bcci {
  &__wrapper {
    display: flex;
    flex-direction: column;
    max-width: 760px;
    margin: 0 auto;
    padding-bottom: 24px;
    gap: 12px;
  }
}
.bcci-content {
  display: flex;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  align-items: center;
  flex: 3 1 100%;
  align-self: stretch;
  padding: 0 12px;
}
.bcci-card {
  border-radius: 14px;
  width: 100%;

  & > div {
    gap: 12px;
  }
}
.bcci-card__title {
  text-transform: uppercase;
  font-size: 14px;
  color: $color-primary;
  margin: 0;
}
// Заголовок дополнительного поля — вопрос организатора целой строкой,
// капслоком он читается как крик.
.bcci-card__title--question {
  text-transform: none;
  font-size: 15px;
  line-height: 1.35;
}
.bcci-extend-list {
  margin: 0;
  padding-left: 18px;

  &__item {
    font-size: 15px;
    line-height: 1.35;
    color: #1f1f1f;

    & + & {
      margin-top: 6px;
    }
  }
}
.bcci-actions {
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding: 0 12px;
}
.bcci-btn {
  flex: 1;
  height: auto;
  min-height: 64px;
  border-radius: 14px;
  border-color: $color-primary;
  color: $color-primary;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 4px;

  &__icon {
    font-size: 22px;
    line-height: 1;
  }

  &__label {
    font-size: 11px;
    line-height: 1.2;
    text-align: center;
    white-space: normal;
  }
}
.bcci-btn__spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: bcci-spin 0.7s linear infinite;
}
@keyframes bcci-spin {
  to { transform: rotate(360deg); }
}
.bcci-no-contacts {
  margin: 0;
  font-size: 14px;
  color: #8a9ab0;
}
.bcci-contact-group {
  display: flex;
  flex-direction: row;
  gap: 12px;

  &__header {
    display: flex;
    align-items: start;
    gap: 12px;
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1.5px solid $color-accent;
    color: $color-accent;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__label {
    font-size: 12px;
    color: #8a9ab0;
  }

  &__value {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 15px;
    color: #1f1f1f;
  }

  &__link {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
